import log from 'loglevel';

const RECOVERY_STORAGE_KEY = 'stale_chunk_recovery';
const RECOVERY_EXPIRY_MS = 60000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /chunkloaderror/i,
];

export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

function hasRecentRecoveryAttempt(): boolean {
  try {
    const stored = sessionStorage.getItem(RECOVERY_STORAGE_KEY);
    if (!stored) {
      return false;
    }
    const { timestamp } = JSON.parse(stored);
    return Date.now() - timestamp < RECOVERY_EXPIRY_MS;
  } catch {
    return false;
  }
}

function markRecoveryAttempt(): void {
  try {
    sessionStorage.setItem(
      RECOVERY_STORAGE_KEY,
      JSON.stringify({ timestamp: Date.now() }),
    );
  } catch {
    // sessionStorage not available
  }
}

async function purgeServiceWorkerAndCaches(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => registration.unregister()),
    );
  }
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
}

/**
 * A failed dynamic import after a deploy means this session references chunk
 * hashes that no longer exist on the CDN — and the service worker keeps
 * serving the stale index that points at them, so a plain reload does not
 * recover. Purge the worker and its caches, then reload once (guarded so a
 * genuinely broken deploy cannot reload-loop).
 *
 * Returns true when a recovery reload was initiated.
 */
export function recoverFromStaleChunks(): boolean {
  if (hasRecentRecoveryAttempt()) {
    return false;
  }
  markRecoveryAttempt();
  purgeServiceWorkerAndCaches()
    .catch((error) => {
      log.error('Stale chunk recovery: cache purge failed', error);
    })
    .finally(() => {
      window.location.reload();
    });
  return true;
}

export function installStaleChunkRecoveryListener(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (recoverFromStaleChunks()) {
      event.preventDefault();
    }
  });
}
